























































































































































import { Component, Provide, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums
import { ActionTypes, UUID } from "@/data/enums";
import { OrgConfigKeys, OrgFeatureKeys } from "@/data/constants";

// Types
import { IState } from "@/store";
import { IBrandGateway, IGateway } from "@/models/gateways";
import { DataModules } from "@/store/modules/data";
import { IBrand } from "@/models/brands";

// Components
import UpmindUpgradeMessage from "@/components/app/admin/upmind/upgradeMessage.vue";

@Component({
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.auth.admin.brandId;
      },
      availableGateways(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.allGatewaysModule,
          scope: this.allGatewaysScope
        });
      },
      activeGateways(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.brandGatewaysModule
        });
      }
    })
  }
})
export default class BrandGatewaysList extends Mixins(RequestPromises) {
  // State
  public brandId!: IBrand["id"];
  public availableGateways!: IGateway[];
  public activeGateways!: IBrandGateway[];

  // Provide
  @Provide()
  public gatewaysProcessing: IGateway["id"][] = [];

  // Variables
  public allGatewaysModule: DataModules = DataModules.GATEWAYS;
  public brandGatewaysModule: DataModules = DataModules.BRAND_GATEWAYS;
  public allGatewaysScope = "$all_gateways";
  public orgConfigKeys = OrgConfigKeys;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  // Computed

  public get upmindUpgradeMessage() {
    return {
      render: (h: CreateElement) => {
        if (this.isLoading) return;
        return h(UpmindUpgradeMessage, {
          class: "has-margin-bottom-100",
          props: {
            featureKey: OrgFeatureKeys.UNLIMITED_PAYMENT_GATEWAYS,
            total: this.sortableData.length
          }
        });
      }
    };
  }

  // Lifecycle methods

  public created() {
    if (!this.contentAvailable) {
      this.isLoading = false;
      return;
    }

    this.loadData();

    this.$bus.$on("gatewaysChanged", async () => {
      await this.getBrandGateways(true);
      await this.getAvailableGateways(true);
      await this.refreshBrandCurrencies();
    });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getAvailableGateways();
      await this.getBrandGateways();
      this.requestError = false;
    } catch (error) {
      this.requestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public getAvailableGateways(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.allGatewaysModule}/list`, {
      scope: this.allGatewaysScope,
      params: {
        brand_id: UUID.UPMIND, // Get all available gateways supported by Upmind
        limit: 0,
        with: "payment_type,application_access_token"
      },
      ignoreStored,
      vm: this
    });
  }

  public getBrandGateways(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.brandGatewaysModule}/list`, {
      brandId: this.brandId,
      params: {
        limit: 0,
        with: "gateway.payment_type,gateway.application_access_token"
      },
      ignoreStored,
      vm: this
    });
  }

  public refreshBrandCurrencies() {
    return this.$store.dispatch("brand/get", { ignoreStored: true });
  }

  public reorder(newOrder: number[]) {
    return this.$store.dispatch(`data/${this.allGatewaysModule}/reorder`, {
      brandId: this.brandId,
      data: {
        gateway_ids: newOrder
      }
    });
  }

  public refreshOrder() {
    return this.getBrandGateways(true);
  }

  public async onGatewayChange(gatewayId: IGateway["id"], payload: object) {
    this.gatewaysProcessing.push(gatewayId);

    try {
      await this.updateGateway(gatewayId, payload);
      await this.getBrandGateways(true);
      await this.refreshBrandCurrencies();

      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_updated", {
          object: this.$t("_.payment_provider")
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("toast/error", {
        message: (error as Error)?.message
      });
    }

    this.$_.remove(this.gatewaysProcessing, gateway => gateway === gatewayId);
  }

  public updateGateway(gatewayId: IGateway["id"], payload: object) {
    return this.$store.dispatch(
      `data/${this.allGatewaysModule}/changeBrandGateway`,
      {
        gatewayId,
        data: {
          brand_id: this.brandId,
          ...payload
        }
      }
    );
  }

  public openUpdateGatewayModal(gateway: IGateway) {
    return this.$store.dispatch(
      `data/${DataModules.GATEWAYS}/openManageGateway`,
      {
        actionType: ActionTypes.UPDATE,
        gateway
      }
    );
  }

  public get sortableData() {
    return this.$_.orderBy(
      this.$_.map(this.availableGateways, gateway => {
        const activeGateway = this.$_.find(
          this.activeGateways,
          activeGateway => activeGateway.gateway_id === gateway.id
        );
        return this.$_.merge({}, gateway, {
          active: this.$_.get(activeGateway, "active", false),
          order: this.$_.get(activeGateway, "order", 0),
          visible: this.$_.get(activeGateway, "visible", false),
          default: this.$_.get(activeGateway, "default", false),
          currencies: this.$_.orderBy(gateway.currencies, [
            "currency_code",
            "asc"
          ])
        });
      }),
      ["active", "order", "name"],
      ["desc", "asc", "asc"]
    );
  }

  public async confirmDelete(gatewayId: IGateway["id"]) {
    if (!this.$userCan("delete_payment_gateway")) {
      return;
    }

    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.payment_method") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            try {
              await this.deleteGateway(gatewayId);

              this.$buefy.toast.open({
                message: this.$t("_.object_successfully_removed", {
                  object: this.$t("_.payment_method")
                }) as string
              });

              this.$bus.$emit("gatewaysChanged");
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
            confirm.close();
          }
        }
      }
    });
  }

  public deleteGateway(gatewayId: IGateway["id"]) {
    return this.$store.dispatch(`data/${DataModules.GATEWAYS}/remove`, {
      id: gatewayId,
      vm: this
    });
  }

  public get canUpdateProviders() {
    return this.$userCan("attach_brand_payment_gateway");
  }

  public get contentAvailable() {
    return this.$userCan("list_brand_gateways");
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.allGatewaysModule,
      scope: this.allGatewaysScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: this.brandGatewaysModule,
      vm: this
    });
  }
}
